// apps/admin/src/components/billing/BillingStatusBadge.tsx
//
// Phase 8.x Debug B3 — single canonical badge for billing-domain status pills.
//
// Before this file the billing pages (Topup, Payouts, Chargebacks, Invoice,
// CreditMemo, Refund, Transactions...) each kept their own ad-hoc Tailwind
// pill helper — usually a `bg-{color}-100 text-{color}-800 border-{color}-200`
// triple that drifted out of sync between pages and looked harsh on Tailwind 4
// (no ring, no inset, no dot indicator).
//
// This component centralises the mapping `domain-status-string -> StatusTone`
// for every billing surface so:
//   • One place to change tone when evolves.
//   • All pills share the smooth soft-tint+ring+dot visual language.
//   • Dark mode is handled by the underlying primitive in one spot.
//
// vg-binding: debug:B3-table-badges
// vg-binding: CONTEXT:UI-SPEC=§4

import type { JSX } from 'react';

import { StatusBadge, type StatusTone } from '../ui/StatusBadge.js';

/**
 * Map a free-form billing-domain status string to a canonical StatusTone.
 * Matches snake_case, PascalCase, and "Title Case" forms — case-insensitive.
 * Returns `'neutral'` for unknown values so the caller never crashes.
 */
export function billingStatusTone(rawStatus: string): StatusTone {
  const s = rawStatus.trim().toLowerCase().replace(/\s+/g, '_');
  switch (s) {
    // Topup / generic approval flow
    case 'pending':
    case 'awaiting_review':
    case 'processing':
    case 'in_review':
    case 'reviewing':
    case 'open':
    case 'investigating':
    case 'disputed':
    case 'submitted':
    case 'queued':
      return 'pending';

    case 'approved':
    case 'completed':
    case 'paid':
    case 'succeeded':
    case 'success':
    case 'settled':
    case 'resolved_won':
    case 'collected':
      return 'approved';

    case 'active':
    case 'enabled':
    case 'live':
      return 'active';

    case 'rejected':
    case 'failed':
    case 'declined':
    case 'error':
    case 'resolved_lost':
    case 'debt_outstanding':
    case 'frozen_pending_recovery':
    case 'overdue':
    case 'past_due':
      return 'rejected';

    case 'suspended':
    case 'on_hold':
    case 'held':
    case 'flagged':
    case 'at_risk':
      return 'at_risk';

    case 'expired':
    case 'voided':
    case 'cancelled':
    case 'canceled':
    case 'refunded':
    case 'reversed':
    case 'closed':
    case 'resolved':
      return 'closed';

    case 'inactive':
    case 'draft':
    case 'unpaid':
      return 'inactive';

    case 'info':
    case 'partial':
    case 'partially_paid':
      return 'info';

    default:
      return 'neutral';
  }
}

/**
 * Title-case a status string for display (e.g. `resolved_won` → `Resolved Won`).
 * Preserves explicit labels supplied by the caller.
 */
export function formatStatusLabel(raw: string): string {
  if (raw.length === 0) return raw;
  // Already Title/Pascal? Keep as-is to honour BE-provided casing
  if (/^[A-Z]/.test(raw) && !raw.includes('_')) return raw;
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

export interface BillingStatusBadgeProps {
  /** Raw status string from BE (any case / shape). */
  status: string;
  /** Override the displayed label — defaults to title-cased `status`. */
  label?: string;
  /** Mark row as flagged/suspicious — bumps tone to `at_risk` regardless of status. */
  flagged?: boolean;
  /** Extra Tailwind utility classes. */
  className?: string;
  /** Hover tooltip. */
  title?: string;
}

/**
 * `<BillingStatusBadge>` — drop-in replacement for the ad-hoc pill helpers
 * scattered across the admin billing pages.
 */
export function BillingStatusBadge(props: BillingStatusBadgeProps): JSX.Element {
  const { status, label, flagged, className, title } = props;
  const tone: StatusTone = flagged ? 'at_risk' : billingStatusTone(status);
  return (
    <StatusBadge
      tone={tone}
      label={label ?? formatStatusLabel(status)}
      className={className}
      title={title}
    />
  );
}

export default BillingStatusBadge;
